import type { Player } from '../types'

export const PlayerBuildSource = {
  Personal: 'personal',
  Guild: 'guild',
} as const

export type PlayerBuildSource = (typeof PlayerBuildSource)[keyof typeof PlayerBuildSource]

export interface PlayerBuildChoice {
  readonly key: string
  readonly source: PlayerBuildSource
  readonly sourceIndex: number
  readonly buildId: string | null
  readonly weaponSlugs: ReadonlyArray<string>
  readonly isAssigned: boolean
}

export interface PlayerSavedWeaponMatch {
  readonly personal: boolean
  readonly guild: boolean
  readonly weaponSlugs: ReadonlyArray<string>
}

export function isDistinctWeaponPair(slugs: ReadonlyArray<string>): boolean {
  return slugs.length === 2
    && slugs[0] !== undefined
    && slugs[1] !== undefined
    && slugs[0].trim().length > 0
    && slugs[1].trim().length > 0
    && slugs[0] !== slugs[1]
}

export function sameWeaponPair(
  first: ReadonlyArray<string>,
  second: ReadonlyArray<string>,
): boolean {
  return isDistinctWeaponPair(first)
    && isDistinctWeaponPair(second)
    && first.every((slug) => second.includes(slug))
}

function uniqueWeaponSlugs(slugs: ReadonlyArray<string>): ReadonlyArray<string> {
  return [...new Set(slugs.filter((slug) => slug.trim() !== ''))]
}

/**
 * Return the exact weapons rendered in the card's current-build control.
 *
 * Legacy raid rows can carry an incomplete `mainWeapons` pair while their
 * current personal library is already available. The card falls back to the
 * selected valid personal build, then the first valid personal build. Filters
 * must use the same projection or a visibly equipped weapon can return zero
 * players.
 */
export function playerCurrentWeaponSlugs(player: Player): ReadonlyArray<string> {
  if (isDistinctWeaponPair(player.mainWeapons)) {
    return uniqueWeaponSlugs(player.mainWeapons)
  }

  const selectedBuild = player.builds[player.activeBuildIndex]
  if (selectedBuild !== undefined && isDistinctWeaponPair(selectedBuild)) {
    return uniqueWeaponSlugs(selectedBuild)
  }

  const fallbackBuild = player.builds.find((build) => isDistinctWeaponPair(build))
  return uniqueWeaponSlugs(fallbackBuild ?? player.mainWeapons)
}

function buildMatchesWeapons(
  build: ReadonlyArray<string>,
  weaponSlugs: ReadonlySet<string>,
): boolean {
  return build.some((slug) => weaponSlugs.has(slug))
}

/**
 * Return valid build choices in the order used when a player joins a raid
 * group. Personal build 1 is the persisted default. Assigned guild builds
 * follow personal builds, while unassigned guild-library rows stay out of
 * automatic selection.
 *
 * When weapon filters are active, matching builds are promoted to the front.
 * Personal builds retain precedence inside each matching/non-matching tier.
 */
export function playerBuildChoices(
  player: Player,
  selectedWeaponSlugs: ReadonlyArray<string> = [],
): ReadonlyArray<PlayerBuildChoice> {
  const personalChoices = player.builds
    .map((build, index): PlayerBuildChoice => ({
      key: `personal-${index}`,
      source: PlayerBuildSource.Personal,
      sourceIndex: index,
      buildId: null,
      weaponSlugs: build,
      isAssigned: true,
    }))
    .filter((choice) => isDistinctWeaponPair(choice.weaponSlugs))
  const guildChoices = player.guildBuilds
    .filter((entry) => entry.isAssigned && isDistinctWeaponPair(entry.weaponSlugs))
    .map((entry): PlayerBuildChoice => ({
      key: `guild-${entry.id}`,
      source: PlayerBuildSource.Guild,
      sourceIndex: entry.orderIndex,
      buildId: entry.id,
      weaponSlugs: entry.weaponSlugs,
      isAssigned: entry.isAssigned,
    }))
  const choices = [...personalChoices, ...guildChoices]
  const selected = new Set(selectedWeaponSlugs)
  if (selected.size === 0) return choices

  const matching = choices.filter((choice) => buildMatchesWeapons(choice.weaponSlugs, selected))
  const remaining = choices.filter((choice) => !buildMatchesWeapons(choice.weaponSlugs, selected))
  return [...matching, ...remaining]
}

export function playerBuildChoiceMatchesWeapons(
  choice: PlayerBuildChoice,
  selectedWeaponSlugs: ReadonlyArray<string>,
): boolean {
  return buildMatchesWeapons(choice.weaponSlugs, new Set(selectedWeaponSlugs))
}

export function firstPlayerBuildChoice(
  player: Player,
  selectedWeaponSlugs: ReadonlyArray<string> = [],
): PlayerBuildChoice | null {
  return playerBuildChoices(player, selectedWeaponSlugs)[0] ?? null
}

export function raidBuildIsSelected(activeBuildIndex: number | undefined): boolean {
  return activeBuildIndex !== undefined && activeBuildIndex >= 0
}

/**
 * Find selected weapons outside the rendered current build. `altWeapons` and
 * Discord-owned personal builds are reported as personal sources; assigned
 * guild builds are reported independently so an unassigned guild-wide library
 * row does not make every player look like they own the same build.
 */
export function playerSavedWeaponMatch(
  player: Player,
  selectedWeaponSlugs: ReadonlyArray<string>,
): PlayerSavedWeaponMatch {
  const selected = new Set(selectedWeaponSlugs)
  if (selected.size === 0) {
    return {
      personal: false,
      guild: false,
      weaponSlugs: [],
    }
  }

  const personalBuilds: ReadonlyArray<ReadonlyArray<string>> = [
    player.altWeapons,
    ...player.builds,
  ]
  const matchingPersonalBuilds = personalBuilds.filter((build) =>
    buildMatchesWeapons(build, selected),
  )
  const matchingGuildBuilds = player.guildBuilds
    .filter((entry) => entry.isAssigned)
    .map((entry) => entry.weaponSlugs)
    .filter((build) => buildMatchesWeapons(build, selected))

  return {
    personal: matchingPersonalBuilds.length > 0,
    guild: matchingGuildBuilds.length > 0,
    weaponSlugs: uniqueWeaponSlugs([
      ...matchingPersonalBuilds.flat(),
      ...matchingGuildBuilds.flat(),
    ]),
  }
}
